/*
 *
 * Loans reducer
 *
 */

import produce from 'immer';

import Platform from '../../../utils/Platform';
import { RESET_USER_STATE } from '../../Main/constants';
import {
  ADD_LOAN,
  ADD_LOAN_FAILURE,
  ADD_LOAN_SUCCESS,
  CANCEL_CHANGE_MAILING_ADDRESS,
  CANCEL_CHANGE_MAILING_ADDRESS_FAILURE,
  CANCEL_CHANGE_MAILING_ADDRESS_SUCCESS,
  CHANGE_INPUT,
  CHANGE_MAILING_ADDRESS,
  CHANGE_MAILING_ADDRESS_FAILURE,
  CHANGE_MAILING_ADDRESS_FORM_TYPE,
  CHANGE_MAILING_ADDRESS_SUCCESS,
  CHANGE_MAILING_ADDRESS_VALIDATION_FAILURE,
  CHANGE_PRIMARY_LOAN,
  CHANGE_PRIMARY_LOAN_FAILURE,
  CHANGE_PRIMARY_LOAN_SUCCESS,
  CHANGE_STEP,
  CHOOSE_DOCUMENT,
  CHOOSE_DOCUMENT_CANCELLATION,
  CHOOSE_DOCUMENT_FAILURE,
  CHOOSE_DOCUMENT_SUCCESS,
  CLOSE_MODAL_STATE,
  DOWNLOAD_DOCUMENT,
  DOWNLOAD_DOCUMENT_FAILURE,
  FETCH_MAILING_ADDRESS,
  FETCH_MAILING_ADDRESS_ALL,
  FETCH_MAILING_ADDRESS_ALL_FAILURE,
  FETCH_MAILING_ADDRESS_ALL_SUCCESS,
  FETCH_MAILING_ADDRESS_FAILURE,
  FETCH_MAILING_ADDRESS_SUCCESS,
  FETCH_MAILING_ADDRESS_VALIDATION_FAILURE,
  INPUT_ERRORS,
  OPEN_MODAL_STATE,
  PROCESS_DOCUMENT,
  PROCESS_DOCUMENT_FAILURE,
  PROCESS_DOCUMENT_SUCCESS,
  RESET_BANNER_ERROR,
  RESET_ERROR_STATE,
  RESET_FORM_STATE,
  RESET_FORM_TYPE,
  RESET_SELECTED_LOAN,
  RESET_STATE,
  RESET_STATUS_STATE,
  RESET_STEP_STATE,
  RESET_UPDATED_PRIMARY_LOAN,
  SET_SELECTED_LOAN,
  UPLOAD_DOCUMENT,
  UPLOAD_DOCUMENT_FAILURE,
  UPLOAD_DOCUMENT_SUCCESS,
} from './constants';

export const initialState = {
  allowedCountries: [],
  allowedProvinces: [],
  allowedStates: [],
  changeMailingAddressFormType: 'United States',
  errors: {
    bannerError: false,
    serviceErrors: {
      addLoan: false,
      mailingAddress: false,
    },
  },
  formErrors: {
    addLoan: {
      isPrimaryLoan: false,
      lastFourSsn: '',
      loanNumber: '',
      propertyZip: '',
    },
    changeMailingAddress: {
      apartment: '',
      city: '',
      company: '',
      state: '',
      streetAddress: '',
      zip: '',
    },
    changeMailingAddressInternational: {
      addressLine2: '',
      addressLine3: '',
      city: '',
      country: '',
      postalCode: '',
      province: '',
      streetAddress: '',
    },
    uploadDocument: { fileInput: '' },
  },
  forms: {
    addLoan: {
      isPrimaryLoan: false,
      lastFourSsn: '',
      loanNumber: '',
      propertyZip: '',
    },
    changeMailingAddress: {
      apartment: '',
      city: '',
      company: '',
      state: '',
      streetAddress: '',
      zip: '',
    },
    changeMailingAddressInternational: {
      addressLine2: '',
      addressLine3: '',
      city: '',
      country: '',
      postalCode: '',
      province: '',
      streetAddress: '',
    },
    uploadDocument: {
      fileInput: {
        fileData: '',
        fileName: '',
        fileSize: 0,
        fileType: '',
      },
    },
  },
  isModalOpen: false,
  loanData: {},
  loanToRefetchMailingAddress: '',
  modalState: '',
  requestStatus: {
    addLoan: 'idle',
    cancelChangeMailingAddress: 'idle',
    changeMailingAddress: 'idle',
    changePrimaryLoan: 'idle',
    chooseDocument: 'idle',
    fetchMailingAddress: 'idle',
    fetchMailingAddressAll: 'idle',
    processDocument: 'idle',
    uploadDocument: 'idle',
  },
  selectedLoan: '',
  step: 1,
  success: '',
  updatedPrimaryLoan: '',
};

/* eslint-disable consistent-return, default-case, no-param-reassign */
const loansReducer = produce((draft, { payload, type }) => {
  switch (type) {
    case ADD_LOAN: {
      draft.errors.bannerError = false;
      draft.errors.serviceErrors.addLoan = false;
      draft.requestStatus.addLoan = 'loading';
      break;
    }
    case ADD_LOAN_FAILURE: {
      const { error } = payload;
      draft.errors.bannerError = error;
      draft.requestStatus.addLoan = 'failed';
      break;
    }
    case ADD_LOAN_SUCCESS: {
      draft.requestStatus.addLoan = 'succeeded';
      break;
    }
    case CANCEL_CHANGE_MAILING_ADDRESS: {
      draft.requestStatus.cancelChangeMailingAddress = 'loading';
      draft.errors.serviceErrors.mailingAddress = false;
      draft.errors.bannerError = false;
      break;
    }
    case CANCEL_CHANGE_MAILING_ADDRESS_FAILURE: {
      draft.requestStatus.cancelChangeMailingAddress = 'failed';
      draft.loanToRefetchMailingAddress = draft.selectedLoan;
      break;
    }
    case CANCEL_CHANGE_MAILING_ADDRESS_SUCCESS: {
      draft.requestStatus.cancelChangeMailingAddress = 'succeeded';
      draft.loanToRefetchMailingAddress = draft.selectedLoan;
      break;
    }
    case CHANGE_INPUT: {
      const { field, form, value } = payload;
      draft.forms[form][field] = value;
      draft.formErrors[form][field] = initialState.formErrors[form][field];
      break;
    }
    case CHANGE_MAILING_ADDRESS: {
      draft.errors.bannerError = false;
      draft.requestStatus.changeMailingAddress = 'loading';
      break;
    }
    case CHANGE_MAILING_ADDRESS_FAILURE: {
      const { error } = payload;
      draft.errors.bannerError = error;
      draft.requestStatus.changeMailingAddress = 'failed';
      break;
    }
    case CHANGE_MAILING_ADDRESS_FORM_TYPE: {
      const { mailingAddressType } = payload;
      draft.changeMailingAddressFormType = mailingAddressType;
      break;
    }
    case CHANGE_MAILING_ADDRESS_SUCCESS: {
      draft.loanToRefetchMailingAddress = draft.selectedLoan;
      draft.requestStatus.changeMailingAddress = 'succeeded';
      break;
    }
    case CHANGE_PRIMARY_LOAN: {
      draft.errors.bannerError = false;
      draft.requestStatus.changePrimaryLoan = 'loading';
      draft.success = '';
      break;
    }
    case CHANGE_PRIMARY_LOAN_FAILURE: {
      const { error } = payload;
      draft.errors.bannerError = error;
      draft.requestStatus.changePrimaryLoan = 'failed';
      draft.selectedLoan = initialState.selectedLoan;
      break;
    }
    case CHANGE_PRIMARY_LOAN_SUCCESS: {
      const { loan, message } = payload;
      draft.requestStatus.changePrimaryLoan = 'succeeded';
      draft.success = message;
      draft.updatedPrimaryLoan = loan;
      break;
    }
    case CHANGE_MAILING_ADDRESS_VALIDATION_FAILURE: {
      const { formToUse, updatedState } = payload;
      draft.formErrors[formToUse] = updatedState;
      draft.requestStatus.changeMailingAddress = 'failed';
      break;
    }
    case CHANGE_STEP: {
      const { step } = payload;
      draft.step = step;
      break;
    }
    case CHOOSE_DOCUMENT: {
      if (Platform.OS === 'ios' || Platform.OS === 'android') {
        // See comment on Settings/Profile equivalent case for reason on commented out line
        // draft.requestStatus.chooseDocument = 'loading';
        draft.requestStatus.chooseDocument = 'idle';
      } else {
        draft.requestStatus.chooseDocument = 'idle';
      }
      break;
    }
    case CHOOSE_DOCUMENT_CANCELLATION: {
      draft.requestStatus.chooseDocument = 'idle';
      break;
    }
    case CHOOSE_DOCUMENT_FAILURE: {
      const { bannerError } = payload;
      draft.errors.bannerError = bannerError;
      draft.forms.uploadDocument.fileInput = initialState.forms.uploadDocument.fileInput;
      draft.requestStatus.chooseDocument = 'failed';
      break;
    }
    case CHOOSE_DOCUMENT_SUCCESS: {
      draft.requestStatus.chooseDocument = 'succeeded';
      break;
    }
    case CLOSE_MODAL_STATE: {
      draft.isModalOpen = initialState.isModalOpen;
      draft.modalState = initialState.modalState;
      break;
    }
    case DOWNLOAD_DOCUMENT: {
      draft.errors.bannerError = false;
      break;
    }
    case DOWNLOAD_DOCUMENT_FAILURE: {
      const { error } = payload;
      draft.errors.bannerError = error;
      break;
    }
    case FETCH_MAILING_ADDRESS: {
      draft.requestStatus.fetchMailingAddress = 'loading';
      draft.errors.bannerError = false;
      draft.errors.serviceErrors.mailingAddress = false;
      draft.loanToRefetchMailingAddress = initialState.loanToRefetchMailingAddress;
      break;
    }
    case FETCH_MAILING_ADDRESS_FAILURE: {
      const { error } = payload;
      draft.requestStatus.fetchMailingAddress = 'failed';
      draft.errors.serviceErrors.mailingAddress = error;
      break;
    }
    case FETCH_MAILING_ADDRESS_SUCCESS: {
      const { addressStatus, loanNumber } = payload;
      draft.loanData[loanNumber] = addressStatus;
      draft.requestStatus.fetchMailingAddress = 'succeeded';
      break;
    }
    case FETCH_MAILING_ADDRESS_VALIDATION_FAILURE: {
      const { error } = payload;
      draft.errors.bannerError = error;
      break;
    }
    case FETCH_MAILING_ADDRESS_ALL: {
      draft.requestStatus.fetchMailingAddressAll = 'loading';
      draft.errors.serviceErrors.mailingAddress = false;
      break;
    }
    case FETCH_MAILING_ADDRESS_ALL_FAILURE: {
      const { error } = payload;
      draft.requestStatus.fetchMailingAddressAll = 'failed';
      draft.errors.serviceErrors.mailingAddress = error;
      break;
    }
    case FETCH_MAILING_ADDRESS_ALL_SUCCESS: {
      const { allAddressData, countryProvinceStateData } = payload;
      Object.keys(allAddressData).forEach((loanNumber) => {
        draft.loanData[loanNumber] = allAddressData[loanNumber];
      });
      const {
        allowedCountries,
        allowedProvinces,
        allowedStates,
      } = countryProvinceStateData;
      draft.allowedCountries = allowedCountries || initialState.allowedCountries;
      draft.allowedProvinces = allowedProvinces || initialState.allowedProvinces;
      draft.allowedStates = allowedStates || initialState.allowedStates;
      draft.requestStatus.fetchMailingAddressAll = 'succeeded';
      break;
    }
    case INPUT_ERRORS: {
      const { form, formErrors } = payload;
      const fields = Object.keys(formErrors);
      // eslint-disable-next-line no-return-assign
      fields.map((field) => draft.formErrors[form][field] = formErrors[field].message || '');
      break;
    }
    case OPEN_MODAL_STATE: {
      const { modalState } = payload;
      draft.isModalOpen = true;
      draft.modalState = modalState;
      break;
    }
    case PROCESS_DOCUMENT: {
      const { file: { name } } = payload;
      draft.requestStatus.processDocument = 'loading';
      draft.errors.bannerError = initialState.errors.bannerError;
      draft.formErrors.uploadDocument = initialState.formErrors.uploadDocument;
      draft.forms.uploadDocument.fileInput = {
        ...initialState.forms.uploadDocument.fileInput,
        fileName: name,
      };
      break;
    }
    case PROCESS_DOCUMENT_FAILURE: {
      const {
        bannerError,
        file,
        validationError,
      } = payload;
      draft.requestStatus.processDocument = 'failed';
      if (bannerError) {
        draft.errors.bannerError = bannerError;
      }
      if (file && validationError) {
        const {
          data: fileData,
          name: fileName,
          size: fileSize,
          type: fileType,
        } = file;
        draft.formErrors.uploadDocument.fileInput = validationError;
        draft.forms.uploadDocument.fileInput.fileData = fileData;
        draft.forms.uploadDocument.fileInput.fileName = fileName;
        draft.forms.uploadDocument.fileInput.fileSize = fileSize;
        // fileType not avaiable on mobile
        if (fileType) {
          draft.forms.uploadDocument.fileInput.fileType = fileType;
        }
      }
      break;
    }
    case PROCESS_DOCUMENT_SUCCESS: {
      const {
        file: {
          data: fileData,
          name: fileName,
          size: fileSize,
          type: fileType,
        },
      } = payload;
      draft.requestStatus.processDocument = 'succeeded';
      draft.forms.uploadDocument.fileInput.fileData = fileData;
      draft.forms.uploadDocument.fileInput.fileName = fileName;
      draft.forms.uploadDocument.fileInput.fileSize = fileSize;
      // Type not avaiable on mobile
      if (fileType) {
        draft.forms.uploadDocument.fileInput.fileType = fileType;
      }
      break;
    }
    case RESET_BANNER_ERROR: {
      draft.errors.bannerError = initialState.errors.bannerError;
      break;
    }
    case RESET_ERROR_STATE: {
      draft.errors = initialState.errors;
      draft.success = initialState.success;
      break;
    }
    case RESET_FORM_STATE: {
      const { formKeys } = payload;
      if (!formKeys) {
        draft.forms = initialState.forms;
        draft.formErrors = initialState.formErrors;
      } else {
        formKeys.forEach((key) => {
          draft.forms[key] = initialState.forms[key];
          draft.formErrors[key] = initialState.formErrors[key];
        });
      }
      break;
    }
    case RESET_FORM_TYPE: {
      draft.changeMailingAddressFormType = initialState.changeMailingAddressFormType;
      break;
    }
    case RESET_SELECTED_LOAN: {
      draft.selectedLoan = initialState.selectedLoan;
      break;
    }
    case RESET_STATE: {
      return initialState;
    }
    case RESET_STATUS_STATE: {
      const { statusKeys } = payload;
      if (!statusKeys) {
        draft.requestStatus = initialState.requestStatus;
      } else {
        statusKeys.forEach((key) => {
          draft.requestStatus[key] = initialState.requestStatus[key];
        });
      }
      break;
    }
    case RESET_STEP_STATE: {
      draft.selectedLoan = initialState.selectedLoan;
      draft.step = initialState.step;
      break;
    }
    case RESET_USER_STATE: {
      return initialState;
    }
    case RESET_UPDATED_PRIMARY_LOAN: {
      draft.updatedPrimaryLoan = '';
      break;
    }
    case SET_SELECTED_LOAN: {
      const { loanNumber } = payload;
      draft.selectedLoan = loanNumber;
      break;
    }
    case UPLOAD_DOCUMENT: {
      draft.requestStatus.uploadDocument = 'loading';
      draft.errors.bannerError = initialState.errors.bannerError;
      draft.formErrors.uploadDocument = initialState.formErrors.uploadDocument;
      break;
    }
    case UPLOAD_DOCUMENT_FAILURE: {
      const { bannerError, validationError } = payload;
      const hasPartialError = !!bannerError || !!validationError;
      draft.requestStatus.uploadDocument = 'failed';
      if (hasPartialError) {
        if (bannerError) {
          draft.errors.bannerError = bannerError;
        }
        if (validationError) {
          draft.formErrors.uploadDocument.fileInput = validationError;
          draft.step -= 1;
        }
      } else {
        // Desktop full error navigation handled here. Mobile is handled in saga.
        draft.step += 2;
      }
      break;
    }
    case UPLOAD_DOCUMENT_SUCCESS: {
      draft.requestStatus.uploadDocument = 'succeeded';
      draft.step += 1;
      break;
    }
  }
}, initialState);

export default loansReducer;
